/**
 * V166.4 — DiagnosticExitIntent
 *
 * Fires when a desktop visitor on /diagnostika-eshopu moves mouse to the
 * top edge (or 25s of dwell on mobile). Offers two paths:
 *   a) "Pošli mi report neskôr"  → POST /api/diagnostic-funnel/exit-capture
 *      → server fires magic-link email + enrols email in ghost drip campaign
 *   b) "Pokračovať v diagnostike" → close popup, focus URL input
 *
 * Session-scoped: shows at most once per browser session.
 */
import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { X, Mail, ArrowRight, CheckCircle2, Loader2, Clock } from "lucide-react";
import { API } from "../../config";

const SESSION_KEY = "trp_diag_exit_shown";

export function DiagnosticExitIntent({ language = "sk", currentUrl = "" }) {
  const [show, setShow] = useState(false);
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const armedRef = useRef(false);

  // Trigger: mouseleave at top of window (desktop) or 25s dwell (mobile)
  const onLeave = useCallback((e) => {
    if (armedRef.current) return;
    if (e.clientY <= 5) {
      armedRef.current = true;
      setShow(true);
      try { sessionStorage.setItem(SESSION_KEY, "1"); } catch { /* noop */ }
    }
  }, []);

  useEffect(() => {
    try { if (sessionStorage.getItem(SESSION_KEY)) return; } catch { /* noop */ }
    if (window.innerWidth >= 768) {
      document.addEventListener("mouseleave", onLeave);
    }
    // Mobile fallback: 25s dwell
    const mobileTimer = window.innerWidth < 768 ? setTimeout(() => {
      if (armedRef.current) return;
      armedRef.current = true;
      setShow(true);
      try { sessionStorage.setItem(SESSION_KEY, "1"); } catch { /* noop */ }
    }, 25000) : null;
    return () => {
      document.removeEventListener("mouseleave", onLeave);
      if (mobileTimer) clearTimeout(mobileTimer);
    };
  }, [onLeave]);

  const close = () => setShow(false);

  const submit = async (e) => {
    e?.preventDefault();
    setError("");
    if (!email.trim() || !/.+@.+\..+/.test(email)) {
      setError("Zadaj platný email");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/diagnostic-funnel/exit-capture`, {
        email: email.trim(),
        url: currentUrl || "",
        language,
      });
      setDone(true);
    } catch {
      // Endpoint is intentionally tolerant — only network failure hits here
      setError("Niečo zlyhalo. Skús znova.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!show) return null;

  return (
    <div
      className="fixed inset-0 z-[120] bg-black/80 backdrop-blur-sm flex items-center justify-center px-4"
      data-testid="diag-exit-overlay"
      onClick={close}
    >
      <div
        className="relative w-full max-w-md bg-gradient-to-b from-zinc-900 to-black border border-red-500/30 rounded-2xl p-6 sm:p-8 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="diag-exit-popup"
      >
        <button
          onClick={close}
          aria-label="Zavrieť"
          data-testid="diag-exit-close"
          className="absolute top-3 right-3 p-1.5 rounded-md text-white/40 hover:text-white hover:bg-white/10"
        >
          <X className="w-4 h-4"/>
        </button>

        {!done ? (
          <>
            <div className="inline-flex items-center gap-2 px-2.5 py-1 mb-4 bg-red-500/10 border border-red-500/30 text-red-300 text-[10px] font-mono uppercase tracking-widest font-bold rounded-full">
              <Clock className="w-3 h-3"/> Počkaj sekundu
            </div>
            <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-white mb-2 leading-[1.15]">
              Nemáš čas teraz?
              <br/>
              <span className="text-red-400">Pošlem ti report neskôr.</span>
            </h2>
            <p className="text-white/60 text-sm mb-5">
              Zadaj email a do 2 minút ti príde <span className="text-white font-bold">magic link</span> — klikneš naň, keď budeš mať čas, a diagnostika prebehne automaticky.
            </p>

            <form onSubmit={submit} className="space-y-3">
              <div className="relative">
                <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40"/>
                <input
                  data-testid="diag-exit-email-input"
                  type="email"
                  autoFocus
                  placeholder="ty@firma.sk"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-9 pr-4 py-3 bg-black/40 border border-white/15 rounded-lg text-white placeholder-white/30 focus:border-red-400 focus:outline-none"
                />
              </div>
              {error && <div className="text-red-400 text-xs" data-testid="diag-exit-error">{error}</div>}
              <button
                data-testid="diag-exit-submit"
                type="submit"
                disabled={submitting}
                className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-red-500 hover:bg-red-400 text-white font-bold text-sm transition-colors disabled:opacity-60"
              >
                {submitting ? (
                  <><Loader2 className="w-4 h-4 animate-spin"/> Posielam…</>
                ) : (
                  <>Pošli mi report neskôr <ArrowRight className="w-4 h-4"/></>
                )}
              </button>
            </form>

            <button
              onClick={close}
              data-testid="diag-exit-dismiss"
              className="w-full mt-3 text-xs text-white/40 hover:text-white/70 underline-offset-2 hover:underline"
            >
              Nie, ďakujem — chcem skenovať teraz
            </button>

            <p className="text-[10px] text-white/30 mt-4 text-center">
              Žiadny spam. Odhlásiš sa jedným klikom.
            </p>
          </>
        ) : (
          <div className="text-center py-2" data-testid="diag-exit-success">
            <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
              <CheckCircle2 className="w-7 h-7 text-emerald-400"/>
            </div>
            <h2 className="text-2xl font-black text-white mb-2">Hotovo. Skontroluj inbox.</h2>
            <p className="text-white/60 text-sm mb-5">
              Poslali sme magic link na <span className="text-white font-mono">{email}</span>.
              Klikni naň kedykoľvek a diagnostika sa otvorí s pred-vyplnenou URL.
            </p>
            <button
              onClick={close}
              className="px-5 py-2.5 rounded-lg bg-white/10 hover:bg-white/20 text-white text-sm font-bold transition-colors"
              data-testid="diag-exit-success-close"
            >
              Zatvoriť
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
